import { PaginationCardMinimal } from "@admin-ui/components/application/pagination/pagination";
import { NativeSelect } from "@admin-ui/components/base/select/select-native";
import { DeleteEntityDialog } from "@admin-ui/components/composed/DeleteEntityDialog";
import { LifecycleBadge } from "@admin-ui/components/composed/LifecycleBadge";
import { TableEmptyState } from "@admin-ui/components/composed/table-empty-state";
import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Avatar } from "@admin-ui/components/primitives/base/avatar/avatar";
import { Badge, BadgeWithDot } from "@admin-ui/components/primitives/base/badges/badges";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import { Toggle } from "@admin-ui/components/primitives/base/toggle/toggle";
import { accountSetupStatusColors, accountTypeColors, formatSetupStatus } from "@admin-ui/utils/account-display";
import { formatDate } from "@admin-ui/utils/format-date";
import { toSortDescriptor } from "@admin-ui/utils/sort";
import { adminAccountPath, profilePath } from "@travily/routes";
import type { AdminAccountsAccount } from "@travily/types/models";
import type AdminAccountsIndex from "@travily/types/pages/AdminAccountsIndex";
import { SearchLg, Trash01, Users01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import type { SortDescriptor } from "react-aria-components";
import { Link, TableBody } from "react-aria-components";

export type AccountFilters = AdminAccountsIndex["filters"];
export type AccountPagy = AdminAccountsIndex["pagy"];
// Derived, not the bare model type: the row carries the commerce trait the
// index asks for, and the delete dialog reads it.
export type AccountRow = AdminAccountsIndex["accounts"][number];

export interface IndexViewProps {
  accounts: AccountRow[];
  pagy: AccountPagy;
  sort: string | null;
  sortDir: string | null;
  filters: AccountFilters;
  onSortChange: (descriptor: SortDescriptor) => void;
  onPageChange: (page: number) => void;
  onSearchChange: (value: string) => void;
  onFilterChange: (key: keyof AccountFilters, value: string) => void;
  onDeleteAccount: (accountId: number, force: boolean) => void;
}

function isPubliclyViewable(account: AccountRow): boolean {
  if (account.visibility !== "public") return false;
  return account.setup_status === "completed" || !account.claimed;
}

const PROFILE_TYPE_OPTIONS = [
  { label: "All types", value: "" },
  { label: "Family", value: "Family" },
  { label: "Community", value: "Community" },
  { label: "Individual", value: "Individual" },
  { label: "Personal", value: "Personal" },
];

const VISIBILITY_OPTIONS = [
  { label: "Any visibility", value: "" },
  { label: "Public", value: "public" },
  { label: "Private", value: "private" },
  { label: "Hidden", value: "hidden" },
];

const SETUP_STATUS_OPTIONS = [
  { label: "Any setup status", value: "" },
  { label: "Not started", value: "not_started" },
  { label: "In progress", value: "in_progress" },
  { label: "Completed", value: "completed" },
];

const CLAIMED_OPTIONS = [
  { label: "Claimed & unclaimed", value: "" },
  { label: "Claimed", value: "true" },
  { label: "Unclaimed", value: "false" },
];

export function IndexView({
  accounts,
  pagy,
  sort,
  sortDir,
  filters,
  onSortChange,
  onPageChange,
  onSearchChange,
  onFilterChange,
  onDeleteAccount,
}: IndexViewProps) {
  const sortDescriptor = toSortDescriptor(sort, sortDir);

  const [searchDraft, setSearchDraft] = useState(filters.search);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (searchDraft === filters.search) return;
    const timer = setTimeout(() => onSearchChange(searchDraft), 300);
    return () => clearTimeout(timer);
  }, [searchDraft, filters.search, onSearchChange]);

  return (
    <>
      <div className="flex flex-col gap-3 rounded-xl bg-secondary p-4 ring-1 ring-secondary ring-inset md:flex-row md:flex-wrap md:items-end md:p-5">
        <div className="md:flex-1 md:min-w-64">
          <Input
            aria-label="Search accounts"
            placeholder="Search by name or handle"
            icon={SearchLg}
            value={searchDraft}
            onChange={setSearchDraft}
            size="sm"
          />
        </div>
        <NativeSelect
          aria-label="Profile type"
          size="sm"
          value={filters.profile_type}
          onChange={(e) => onFilterChange("profile_type", e.target.value)}
          options={PROFILE_TYPE_OPTIONS}
          className="md:w-44"
        />
        <NativeSelect
          aria-label="Visibility"
          size="sm"
          value={filters.visibility}
          onChange={(e) => onFilterChange("visibility", e.target.value)}
          options={VISIBILITY_OPTIONS}
          className="md:w-40"
        />
        <NativeSelect
          aria-label="Setup status"
          size="sm"
          value={filters.setup_status}
          onChange={(e) => onFilterChange("setup_status", e.target.value)}
          options={SETUP_STATUS_OPTIONS}
          className="md:w-44"
        />
        <NativeSelect
          aria-label="Claim state"
          size="sm"
          value={filters.claimed}
          onChange={(e) => onFilterChange("claimed", e.target.value)}
          options={CLAIMED_OPTIONS}
          className="md:w-48"
        />
        <Toggle
          size="sm"
          label="Include Personal"
          isSelected={filters.include_personal === "true"}
          onChange={(selected) => onFilterChange("include_personal", selected ? "true" : "")}
        />
      </div>

      <TableCard.Root size="sm">
        <Table aria-label="Accounts" sortDescriptor={sortDescriptor} onSortChange={onSortChange}>
          <Table.Header>
            <Table.Head id="profile_name" label="Name" isRowHeader allowsSorting className="w-full max-w-1/3" />
            <Table.Head id="handle" label="Handle" allowsSorting />
            <Table.Head id="type" label="Type" />
            <Table.Head id="profile_visibility" label="Visibility" allowsSorting />
            <Table.Head id="setup_status" label="Setup" allowsSorting />
            <Table.Head id="created_at" label="Created" allowsSorting />
            <Table.Head id="actions" label="" />
          </Table.Header>
          <TableBody items={accounts} renderEmptyState={() => <EmptyState />}>
            {(account: AccountRow) => (
              <Table.Row key={account.id} id={account.id}>
                <Table.Cell>
                  <span className="flex items-center gap-2">
                    <Link
                      href={adminAccountPath(account.id)}
                      className="flex items-center gap-3 text-sm font-medium text-brand-600 hover:text-brand-700"
                    >
                      <Avatar
                        src={account.avatar?.src ?? null}
                        alt={account.display_name ?? "Account"}
                        initials={(account.display_name ?? "?").charAt(0).toUpperCase()}
                        size="sm"
                      />
                      <span className="whitespace-nowrap">{account.display_name ?? "-"}</span>
                    </Link>
                    {account.lifecycle_state === "anonymized" && (
                      <LifecycleBadge lifecycleState={account.lifecycle_state} anonymizedAt={account.anonymized_at} />
                    )}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {account.handle && isPubliclyViewable(account) ? (
                    <Link
                      href={profilePath(account.handle)}
                      target="_blank"
                      className="text-brand-600 hover:text-brand-700"
                    >
                      @{account.handle}
                    </Link>
                  ) : account.handle ? (
                    `@${account.handle}`
                  ) : (
                    "-"
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    color={accountTypeColors[account.profile_type as keyof typeof accountTypeColors] ?? "gray"}
                    size="sm"
                  >
                    {account.profile_type?.toLowerCase() ?? "unknown"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Badge color={visibilityBadgeColor(account.visibility)} size="sm" type="pill-color">
                    {account.visibility ? capitalize(account.visibility) : "-"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <BadgeWithDot color={accountSetupStatusColors[account.setup_status]} size="sm" type="pill-color">
                    {formatSetupStatus(account.setup_status)}
                  </BadgeWithDot>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">{formatDate(account.created_at)}</Table.Cell>
                <Table.Cell>
                  {account.lifecycle_state !== "anonymized" && (
                    <Dropdown.Root>
                      <Dropdown.DotsButton />
                      <Dropdown.Popover className="w-min">
                        <Dropdown.Menu
                          onAction={(key) => {
                            if (key === "delete") setPendingDeleteId(account.id);
                          }}
                        >
                          <Dropdown.Item id="delete" icon={Trash01} label="Delete account" />
                        </Dropdown.Menu>
                      </Dropdown.Popover>
                    </Dropdown.Root>
                  )}
                </Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      </TableCard.Root>

      <PaginationCardMinimal align="right" page={pagy.page} total={pagy.pages} onPageChange={onPageChange} />

      {pendingDeleteId != null &&
        (() => {
          const account = accounts.find((a) => a.id === pendingDeleteId);
          if (!account) return null;
          return (
            <DeleteEntityDialog
              entityLabel="account"
              entityName={account.display_name}
              deletionEffect={account.deletion_effect}
              commerceCount={account.commerce_count}
              isOpen
              onOpenChange={(open) => {
                if (!open) setPendingDeleteId(null);
              }}
              onConfirm={(force) => {
                onDeleteAccount(account.id, force);
                setPendingDeleteId(null);
              }}
            />
          );
        })()}
    </>
  );
}

function visibilityBadgeColor(visibility: AdminAccountsAccount["visibility"]): "success" | "warning" | "gray" {
  if (visibility === "public") return "success";
  if (visibility === "private") return "warning";
  return "gray";
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function EmptyState() {
  return <TableEmptyState icon={Users01} title="No accounts match" description="Try clearing some filters." />;
}
